import axios from "axios";
import { ADMIN_API_URL, SHOPIFY_OAUTH_PATH } from "../config/serviceConstant";

// Base URL for OAuth routes: <admin api>/auth/shopify/oauth2/<endpoint>
const OAUTH_BASE = `${ADMIN_API_URL}${SHOPIFY_OAUTH_PATH}`;

/**
 * Authenticate Shopify using Admin API access token and store domain
 * headers must contain: orgid, connname, apikey, datacenterurl, datacenter
 */
export const authenticateShopify = async ({
  accessToken,
  storeDomain,
  headers,
}) => {
  try {
    const config = {
      method: "POST",
      url: ADMIN_API_URL + "/auth/shopify/authenticate",
      data: {
        shopifyApiKey: accessToken,
        storeDomain: storeDomain,
      },
      headers,
    };
    const resp = await axios(config);
    return resp?.data || null;
  } catch (error) {
    console.error("authenticateShopify error:", error);
    return null;
  }
};

/**
 * Verify Shopify authentication status
 */
export const verifyShopifyAuth = async (headers) => {
  try {
    const config = {
      method: "GET",
      url: ADMIN_API_URL + "/auth/shopify/verifyauth",
      headers,
    };
    const resp = await axios(config);
    if (resp?.data?.shop || resp?.data?.data) {
      return resp.data;
    }
    return null;
  } catch (error) {
    console.error("verifyShopifyAuth error:", error);
    return null;
  }
};

/**
 * Revoke Shopify authentication
 */
export const revokeShopify = async (headers) => {
  try {
    const config = {
      method: "GET",
      url: ADMIN_API_URL + "/auth/shopify/revoke",
      headers,
    };
    const resp = await axios(config);
    if (resp?.data || !resp?.error) return true;
    return false;
  } catch (error) {
    console.error("revokeShopify error:", error);
    return false;
  }
};

/**
 * Fetch all stored Shopify tokens / connected stores for this org.
 * Route: admin/v3/auth/shopify/tokens
 *
 * Normalizes each item so callers can rely on `.domain` and `.name`
 * regardless of whether the backend returns `shop`, `shopDomain`, etc.
 */
export const getShopifyStores = async (headers) => {
  try {
    const config = {
      method: "GET",
      url: ADMIN_API_URL + "/auth/shopify/tokens",
      headers,
    };
    const resp = await axios(config);
    const raw =
      resp?.data?.tokens ||
      resp?.data?.stores ||
      resp?.data?.data ||
      [];
    return raw
      .map((item) => {
        const domain =
          item.domain ||
          item.shop ||
          item.shopDomain ||
          item.storeDomain ||
          item.shopifyshopurl ||
          "";
        return {
          ...item,
          domain,
          name:
            item.name ||
            item.storeName ||
            item.shopName ||
            domain.replace(/\.myshopify\.com$/, ""),
        };
      })
      .filter((item) => item.domain);
  } catch (error) {
    console.error("getShopifyStores error:", error);
    return [];
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// OAuth flow (Partner Custom App + OAuth)
// Replaces the legacy "Develop apps" paste-token walkthrough.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Register the merchant's Zoho identity with the backend's short-lived cache
 * (TTL 15 min), keyed by shop. Must be called before redirecting to /install.
 *
 * @param shop    Full myshopify domain, e.g. "rayhancloth.myshopify.com"
 * @param headers orgid / apikey / connname / datacenter / datacenterurl
 * @returns { ok: boolean, error?: string, data?: object }
 */
export const preInstallShopifyOAuth = async (shop, headers) => {
  try {
    const resp = await axios({
      method: "POST",
      url: `${OAUTH_BASE}/pre-install`,
      params: { shop },
      headers,
    });
    const body = resp?.data || {};
    if (body.error) return { ok: false, error: body.error };
    return { ok: true, data: body.data || body };
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      "pre-install failed";
    console.error("preInstallShopifyOAuth error:", error);
    return { ok: false, error: msg };
  }
};

/**
 * Build the URL the browser must top-level redirect to in order to start
 * the Shopify OAuth flow. Do NOT fetch — set `window.location.href`.
 */
export const buildShopifyOAuthInstallUrl = (shop) =>
  `${OAUTH_BASE}/install?shop=${encodeURIComponent(shop)}`;

/**
 * Claim the install once the merchant has approved on Shopify. Fired when
 * the post-install UI dialog closes so the backend can mark the pre-install
 * cache entry as consumed (or fetch the token if claim drives the exchange).
 *
 * @param shop    Full myshopify domain
 * @param headers same per-store auth headers as pre-install
 * @returns { ok: boolean, error?: string, data?: object }
 */
export const claimShopifyOAuth = async (shop, headers) => {
  try {
    const resp = await axios({
      method: "GET",
      url: `${OAUTH_BASE}/claim`,
      params: { shop },
      headers,
    });
    const body = resp?.data || {};
    if (body.error) return { ok: false, error: body.error };
    return { ok: true, data: body.data || body };
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      "claim failed";
    console.error("claimShopifyOAuth error:", error);
    return { ok: false, error: msg };
  }
};

/**
 * Verify the stored OAuth token still works against Shopify.
 * @returns { valid: boolean, shop?: object, error?: string }
 */
export const verifyShopifyOAuthAuth = async (shop, headers) => {
  try {
    const resp = await axios({
      method: "GET",
      url: `${OAUTH_BASE}/verifyauth`,
      params: { shop },
      headers,
    });
    const body = resp?.data || {};
    if (body.error || !body.data) {
      return { valid: false, error: body.error || "Token is no longer valid" };
    }
    return { valid: true, shop: body.data.shop || body.data };
  } catch (error) {
    const msg =
      error?.response?.data?.error || error?.message || "verifyauth failed";
    console.error("verifyShopifyOAuthAuth error:", error);
    return { valid: false, error: msg };
  }
};

/**
 * Revoke the stored OAuth token for one shop.
 * @returns { ok: boolean, error?: string }
 */
export const revokeShopifyOAuth = async (shop, headers) => {
  try {
    const resp = await axios({
      method: "GET",
      url: `${OAUTH_BASE}/revoke`,
      params: { shop },
      headers,
    });
    const body = resp?.data || {};
    if (body.error) return { ok: false, error: body.error };
    return { ok: true };
  } catch (error) {
    const msg =
      error?.response?.data?.error || error?.message || "revoke failed";
    console.error("revokeShopifyOAuth error:", error);
    return { ok: false, error: msg };
  }
};

/**
 * List all connected stores under this org via the OAuth tokens route.
 * Returns the same `{ domain, name, ...item }` shape as getShopifyStores
 * so the UI can swap providers without changing the card rendering.
 */
export const getShopifyOAuthStores = async (headers) => {
  try {
    const resp = await axios({
      method: "GET",
      url: `${OAUTH_BASE}/tokens`,
      headers,
    });
    const body = resp?.data || {};
    const raw = Array.isArray(body.data) ? body.data : [];
    return raw
      .map((item) => {
        const domain =
          item.storeDomain ||
          item.shop ||
          item.shopDomain ||
          item.domain ||
          item.shopifyshopurl ||
          "";
        return {
          ...item,
          domain,
          name:
            item.name ||
            item.storeName ||
            item.shopName ||
            domain.replace(/\.myshopify\.com$/, ""),
          authType: item.authType || "OAUTH",
          installedAt: item.installedAt || null,
          scope: item.scope || "",
        };
      })
      .filter((item) => item.domain);
  } catch (error) {
    console.error("getShopifyOAuthStores error:", error);
    return [];
  }
};

/**
 * Pre-uninstall cleanup. One org-level call (connname = `<ext>__shopify`)
 * that the backend uses to, across every connected shop:
 *   - delete every webhook
 *   - revoke the Shopify access token + delete the token rows
 *   - delete all settings for this org + extension
 *
 * Mirrors the Deluge pre-uninstall step. Headers must carry orgid / apikey /
 * connname (org-level Shopify) + datacenter context.
 *
 * @returns { ok: boolean, data?: object, error?: string }
 */
export const preUninstallShopify = async (headers) => {
  try {
    const resp = await axios({
      method: "POST",
      url: `${OAUTH_BASE}/pre-uninstall`,
      headers,
    });
    return { ok: true, data: resp?.data ?? null };
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      "pre-uninstall failed";
    console.error("preUninstallShopify error:", error);
    return { ok: false, error: msg };
  }
};
